import { ref, Ref } from 'vue'
import { FileInfo, FileStatus } from '../UploaderTypes'

interface PropsUseDataTransfer {
  currentPath: string
  currentBasePath: string
}
interface ResponseUseDataTransfer {
  fileList: Ref<FileInfo[]>
  clearFiles: () => void
  extractFilesFromDataTransfer: (event: DragEvent) => Promise<void>
}

export default function useDataTransfer(props: PropsUseDataTransfer): ResponseUseDataTransfer {
  const { currentPath, currentBasePath } = props
  const fileList = ref<FileInfo[]>([])

  async function extractFilesFromDataTransfer(event: DragEvent) {
    const items = event.dataTransfer?.items

    if (items) {
      await Promise.all(
        Array.from(items).map(async (item) => {
          const entry = item.webkitGetAsEntry?.()
          if (entry) {
            await processEntry({ entry, path: currentPath, basePath: currentBasePath })
          }
        })
      )
    } else if (event.dataTransfer?.files) {
      Array.from(event.dataTransfer.files).forEach((file) => {
        fileList.value.push({
          name: file.name,
          type: file.type,
          file,
          isDirectory: false,
          status: FileStatus.PREPARE,
          path: currentPath,
          basePath: currentBasePath
        })
      })
    }
  }

  const processEntry = (props: any): Promise<void> => {
    const { entry, path, basePath, parent = '' } = props
    return new Promise((resolve, reject) => {
      try {
        if (entry.isFile) {
          ;(entry as FileSystemFileEntry).file((file: File) => {
            fileList.value.push({
              name: file.name,
              type: file.type,
              file,
              isDirectory: false,
              status: FileStatus.PREPARE,
              path,
              basePath,
              parent
            })
            resolve()
          })
        } else if (entry.isDirectory) {
          const reader = (entry as FileSystemDirectoryEntry).createReader()
          reader.readEntries(async (entries: FileSystemEntry[]) => {
            const { name } = entry
            fileList.value.push({
              name,
              type: 'directory',
              isDirectory: true,
              status: FileStatus.PREPARE,
              path,
              basePath,
              parent
            })
            const childrenPath = `${path}/${name}/`.replace(/^\/|\/{2,}/g, (match) => (match === '/' ? '' : '/'))
            await Promise.all(
              entries.map((subEntry) =>
                processEntry({ entry: subEntry, path: childrenPath, parent: name, basePath: currentBasePath })
              )
            )
            resolve()
          })
        }
      } catch (error) {
        reject(error)
      }
    })
  }

  const clearFiles = () => {
    fileList.value = []
  }

  return {
    fileList,
    clearFiles,
    extractFilesFromDataTransfer
  }
}
